import React, { useRef } from "react";
import { useController, useFormContext, FieldError } from "react-hook-form";
import { toast } from "react-toastify";

import { cn } from "~/utils/tailwind";

//how to pass file name  in icustomfileinput props

interface ICustomFileInputProps {
  name: string;
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
}

const CustomFileInput: React.FC<ICustomFileInputProps> = ({
  name,
  label,
  isRequired,
  disabled = false,
}) => {
  const { getValues, watch } = useFormContext();
  console.log(getValues(), watch());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const { field } = useController({ name, control });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileType = file.type;
      const fileName = file.name;
      const isValidPDF =
        fileType === "application/pdf" &&
        fileName.toLowerCase().endsWith(".pdf");
      const isValidTIFF =
        (fileType === "image/tiff" || fileType === "image/x-tiff") &&
        (fileName.toLowerCase().endsWith(".tif") ||
          fileName.toLowerCase().endsWith(".tiff"));

      // Clear previous errors
      clearErrors(`${name}_content`);

      if (isValidPDF || isValidTIFF) {
        // If valid, process the file
        field.onChange(file);
        console.log(file);
        const base64 = await fileToBase64(file);
        const chunkedBase64 = base64.split(",")[1];
        setValue(`${name}_content`, chunkedBase64, { shouldValidate: true });

        if (watch("file_extension") === "pdf") {
          if (!isValidPDF) {
            toast.error("Please select a pdf  file", {
              theme: "colored",
            });
            return;
          }
        } else if (watch("file_extension") === "tif") {
          if (!isValidTIFF) {
            toast.error("Please select a tif  file", {
              theme: "colored",
            });
            return;
          }
        }
      }

      // Set a specific error message
      else {
        setError(`${name}_content`, {
          type: "manual",
          message: "Only PDF and TIFF files are allowed.",
        });
      }
    }
  };

  const fileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
    });
  };

  return (
    <div className="relative group">
      {label && (
        <label
          htmlFor={name}
          className="absolute -top-2 left-2 inline-block bg-neutral-100 px-1 text-[0.875rem] font-medium text-neutral-600"
        >
          <span className={cn(errors[`${name}_content`] && "text-red-700")}>
            {label} {isRequired && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}
      <input
        type="text"
        className={cn(
          "block w-full h-12 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
          errors[`${name}_content`] && "border-red-700 border-solid border-2",
          disabled && "cursor-not-allowed focus:ring-gray-300 focus:ring-1"
        )}
        value={typeof field.value?.name === "string" ? field.value?.name : ""}
        placeholder="No file chosen"
        readOnly
        onClick={() => {
          !disabled && fileInputRef.current?.click();
        }}
      />
      <button
        type="button"
        className={cn(
          "absolute right-0 top-0 mt-2 mr-2 px-4 py-1.5 bg-primary rounded-md text-white text-sm",
          disabled && "cursor-not-allowed"
        )}
        onClick={() => {
          !disabled && fileInputRef.current?.click();
        }}
      >
        Browse
      </button>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
      />
      {errors[`${name}_content`] && (
        <p className="mt-1 text-xs text-red-700">
          {(errors[`${name}_content`] as FieldError).message}
        </p>
      )}
    </div>
  );
};

export default CustomFileInput;
